import { fetchSpliceSampleFile } from "@/client/utils/createSpliceSampleInputFile";
import { readSampleDragPayload } from "@/client/utils/sample-drag-payload";
import type { SpliceSearchResult } from "@/shared/splice-search.api";

export interface DropHandlers {
    onEnter?: (event: DragEvent) => void;
    onOver?: (event: DragEvent) => void;
    onLeave?: (event: DragEvent) => void;
    onDrop?: (file: File | undefined, event: DragEvent) => void;
    /**
     * A catalog sample dragged out of the Sounds browser panel (see
     * `sample-drag-payload.ts`). A target that implements this gets the
     * search result and can store the preview with its catalog metadata; one
     * that does not still gets the preview through `onDrop`, fetched and
     * named after the sample, so every existing drop target accepts samples
     * without knowing they exist.
     */
    onDropSample?: (result: SpliceSearchResult, event: DragEvent) => void;
}

export const addFileDropHandling = (dropZoneEl: HTMLElement, handlers: DropHandlers) => {
    dropZoneEl.addEventListener("dragenter", (event) => {
        event.preventDefault();
        handlers.onEnter?.(event);
    });

    dropZoneEl.addEventListener("dragover", (event) => {
        event.preventDefault();
        handlers.onOver?.(event);
    });

    dropZoneEl.addEventListener("dragleave", (event) => {
        handlers.onLeave?.(event);
    });

    dropZoneEl.addEventListener("drop", (event) => {
        event.preventDefault();
        // Read before anything asynchronous: the data store is only readable
        // for the duration of the event.
        const sample = readSampleDragPayload(event.dataTransfer);
        if (sample !== undefined) {
            if (handlers.onDropSample !== undefined) {
                handlers.onDropSample(sample, event);
                return;
            }
            if (handlers.onDrop !== undefined) {
                const onDrop = handlers.onDrop;
                void fetchSpliceSampleFile(sample)
                    .then((file) => onDrop(file, event))
                    .catch(() => onDrop(undefined, event));
                return;
            }
        }
        const files = Array.from(event.dataTransfer?.files ?? []);
        const file = files[0];
        handlers.onDrop?.(file, event); // TODO upgrade handlers to handle multiple files
    });
};
